// ChangePasswordPage - change password; also the forced landing for
// freshly-provisioned operators carrying must_change_password.

import { Loader2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { ApiError, api } from '@/lib/api';

export default function ChangePasswordPage() {
  const { user, refresh } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const forced = !!user?.must_change_password;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (pw.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    if (pw === current) {
      toast.error('New password must differ from the current one');
      return;
    }
    setSubmitting(true);
    try {
      await api.changePassword(current, pw);
      // Refetch /me so the cleared must_change_password flag propagates
      // and the routing in App.tsx stops redirecting back here.
      await refresh();
      toast.success('Password updated');
      nav('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        toast.error('Current password is incorrect');
      } else if (err instanceof ApiError && err.status === 400) {
        toast.error(err.message || 'Invalid request');
      } else {
        toast.error('Could not change password');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title={forced ? 'Set a new password' : 'Change password'}>
      <div className="card p-7">
        {forced && (
          <div className="mb-5 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
            This account was provisioned with a temporary password. Choose a
            new one to continue.
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              required
              className="input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div>
            <label className="label">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              className="input"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 12 characters"
            />
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm && pw !== confirm && (
              <div className="error-text">Passwords don't match.</div>
            )}
          </div>
          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Update password
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
